import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">文章不存在</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium text-sm cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const canonicalUrl = `https://jiuqugan.com/article/${slug}`;

  return (
    <>
      <SEOHead
        title={`${article.title} - 就去干行动力觉醒`}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',就去干,行动力,拖延症'}
        canonicalUrl={canonicalUrl}
        ogType="article"
        ogImage={article.image}
        article={article}
      />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <article className="max-w-3xl mx-auto px-4 md:px-6 pb-16 md:pb-24">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category
                  ? [{ label: category.name, href: `/category/${category.slug}` }]
                  : []),
                { label: article.title },
              ]}
            />

            <div className="py-6 md:py-10">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-6">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                  {article.readTime}
                </span>
              </div>

              <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                  prose-h3:text-lg md:prose-h3:text-xl prose-h3:font-heading prose-h3:font-bold prose-h3:text-foreground-950 prose-h3:mt-8 prose-h3:mb-4
                  prose-p:my-4 prose-p:leading-relaxed
                  prose-strong:text-foreground-950 prose-strong:font-semibold
                  [&_p:last-child]:text-primary-600 [&_p:last-child]:font-bold [&_p:last-child]:text-lg"
                dangerouslySetInnerHTML={{ __html: article.contentHtml }}
              />
            </div>

            <div className="mt-8 pt-8 border-t border-background-200">
              <h4 className="text-sm font-semibold text-foreground-950 mb-4">分享这篇文章</h4>
              <div className="flex items-center gap-3">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(canonicalUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-600 hover:bg-foreground-200 transition-colors cursor-pointer"
                  aria-label="分享到 Twitter"
                >
                  <i className="ri-twitter-x-line w-4 h-4 flex items-center justify-center"></i>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-600 hover:bg-foreground-200 transition-colors cursor-pointer"
                  aria-label="分享到 Facebook"
                >
                  <i className="ri-facebook-line w-4 h-4 flex items-center justify-center"></i>
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-600 hover:bg-foreground-200 transition-colors cursor-pointer"
                  aria-label="分享到 LinkedIn"
                >
                  <i className="ri-linkedin-line w-4 h-4 flex items-center justify-center"></i>
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(canonicalUrl);
                  }}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-100 text-foreground-600 hover:bg-foreground-200 transition-colors cursor-pointer"
                  aria-label="复制链接"
                >
                  <i className="ri-link w-4 h-4 flex items-center justify-center"></i>
                </button>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}